import React, {useState, useEffect, useCallback, useMemo} from 'react';
import Avatar from '@cloudscape-design/chat-components/avatar';
import {Box, Button, Header, SpaceBetween, SplitPanel, Textarea} from '@cloudscape-design/components';
import AppLayout from '@cloudscape-design/components/app-layout';
import {useChat} from '@ai-sdk/react';
import {useStickToBottom} from 'use-stick-to-bottom';
import ChatPanel from './chat-panel';
import ChatMessage from './chat-message';
import {Chat, CassandraChatTransport, listChats, createChat, deleteChat, getMessages, setUserId} from './cassandra-api';
import {useAuth, ServiceResource} from '../hooks/use-auth';
import './cassandra.css';

export default function CassandraChat() {
    const auth = useAuth();
    const credentials = auth.authInformation!.getCurrentServiceEndpoint(ServiceResource.ExplainabilityView).credentials;
    const userAlias = auth.authInformation?.email?.split('@')[0];

    useEffect(() => {
        if (auth.authInformation?.email) setUserId(auth.authInformation.email);
    }, [auth.authInformation?.email]);

    const [chats, setChats] = useState<Chat[]>([]);
    const [chatsLoading, setChatsLoading] = useState(true);
    const [activeChatId, setActiveChatId] = useState<string | null>(null);
    const [input, setInput] = useState('');
    const [splitPanelOpen, setSplitPanelOpen] = useState(false);
    const [splitPanelContent] = useState<string>('');
    const {scrollRef, contentRef, isAtBottom, scrollToBottom} = useStickToBottom();

    const transport = useMemo(() => (credentials ? new CassandraChatTransport(credentials) : null), [credentials]);

    const {messages, sendMessage, status, setMessages} = useChat({
        id: activeChatId ?? undefined,
        transport: transport ?? undefined,
    });

    const sending = status === 'streaming' || status === 'submitted';

    // Load chat list
    useEffect(() => {
        if (!credentials) return;
        setChatsLoading(true);
        listChats(credentials)
            .then(setChats)
            .catch(() => setChats([]))
            .finally(() => setChatsLoading(false));
    }, [credentials]);

    // Load messages when switching chats
    useEffect(() => {
        if (!activeChatId || !credentials) {
            setMessages([]);
            return;
        }
        if (sending) return;
        getMessages(activeChatId, credentials).then((msgs) => setMessages(msgs));
    }, [activeChatId, credentials]);

    const refreshChats = useCallback(() => {
        if (!credentials) return;
        listChats(credentials)
            .then(setChats)
            .catch(() => {});
    }, [credentials]);

    const handleNewChat = useCallback(() => {
        setActiveChatId(null);
        setMessages([]);
        setInput('');
    }, [setMessages]);

    const handleSelectChat = useCallback(
        (chatId: string) => {
            setActiveChatId(chatId);
            if (transport) transport.setChatId(chatId);
        },
        [transport]
    );

    const handleDeleteChat = useCallback(
        async (chatId: string) => {
            if (!credentials) return;
            await deleteChat(chatId, credentials);
            if (activeChatId === chatId) {
                setActiveChatId(null);
                setMessages([]);
            }
            refreshChats();
        },
        [credentials, activeChatId, refreshChats, setMessages]
    );

    const handleSend = useCallback(async () => {
        const text = input.trim();
        if (!text || sending || !credentials || !transport) return;

        let chatId = activeChatId;
        if (!chatId) {
            const chat = await createChat(text.slice(0, 50), credentials);
            chatId = chat.id;
            setActiveChatId(chatId);
            refreshChats();
        }

        transport.setChatId(chatId);
        setInput('');
        sendMessage({text});
    }, [input, sending, activeChatId, credentials, refreshChats, sendMessage, transport]);

    // Refresh chat list when streaming finishes
    useEffect(() => {
        if (status === 'ready' && activeChatId) {
            refreshChats();
        }
    }, [status]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <AppLayout
            navigationWidth={260}
            navigation={
                <ChatPanel
                    chats={chats}
                    activeChatId={activeChatId}
                    loading={chatsLoading}
                    onSelectChat={handleSelectChat}
                    onNewChat={handleNewChat}
                    onDeleteChat={handleDeleteChat}
                />
            }
            splitPanel={
                splitPanelOpen ? (
                    <SplitPanel header="Artifacts" closeBehavior="hide">
                        <Box padding="s">
                            {splitPanelContent ? (
                                <pre style={{whiteSpace: 'pre-wrap', fontSize: 13}}>{splitPanelContent}</pre>
                            ) : (
                                <Box color="text-body-secondary">Tool outputs and artifacts will appear here.</Box>
                            )}
                        </Box>
                    </SplitPanel>
                ) : undefined
            }
            splitPanelOpen={splitPanelOpen}
            onSplitPanelToggle={({detail}) => setSplitPanelOpen(detail.open)}
            splitPanelPreferences={{position: 'side'}}
            toolsHide
            content={
                <div className="cassandra-main">
                    <Header variant="h2" description="S&OP Forecasting Assistant">
                        Cassandra
                    </Header>
                    <div className="cassandra-messages" ref={scrollRef} role="log">
                        {messages.length === 0 && !sending && (
                            <div className="cassandra-empty-state">
                                <Avatar color="gen-ai" iconName="gen-ai" ariaLabel="Cassandra" width={48} />
                                <Box variant="h3">How can I help you today?</Box>
                                <Box color="text-body-secondary" textAlign="center">
                                    Ask about demand forecasts, S&OP data, or site volumes.
                                </Box>
                            </div>
                        )}
                        <div ref={contentRef} className="max-w-[900px] mx-auto px-4">
                            <SpaceBetween size="xs">
                                {messages.map((msg, i) => (
                                    <ChatMessage
                                        key={msg.id}
                                        message={msg}
                                        userAlias={userAlias}
                                        isStreaming={sending && msg.role === 'assistant' && i === messages.length - 1}
                                    />
                                ))}
                            </SpaceBetween>
                        </div>
                        {!isAtBottom && (
                            <div style={{position: 'sticky', bottom: 32, float: 'right', marginRight: '5%'}}>
                                <Button
                                    variant="icon"
                                    iconName="angle-down"
                                    onClick={() => scrollToBottom()}
                                    ariaLabel="Scroll to bottom"
                                />
                            </div>
                        )}
                    </div>
                    <div className="cassandra-input-area max-w-[900px] mx-auto w-full px-4">
                        <div className="cassandra-prompt-container" onKeyDown={handleKeyDown}>
                            <Textarea
                                value={input}
                                onChange={({detail}) => setInput(detail.value)}
                                placeholder="Type your message here... (Enter to send, Shift+Enter for new line)"
                                disabled={sending}
                                rows={2}
                            />
                            <div className="cassandra-prompt-actions">
                                <Button
                                    variant="icon"
                                    iconName="view-vertical"
                                    ariaLabel="Toggle artifacts panel"
                                    onClick={() => setSplitPanelOpen(!splitPanelOpen)}
                                />
                                <Button
                                    variant="primary"
                                    iconName="send"
                                    ariaLabel="Send message"
                                    onClick={handleSend}
                                    disabled={!input.trim() || sending}
                                />
                            </div>
                        </div>
                        <div className="cassandra-disclaimer">Cassandra can make mistakes. Check important info.</div>
                    </div>
                </div>
            }
        />
    );
}
